import { BUILD_VERSION } from "@/lib/cache/build-version.generated";

/**
 * sessionStorage key for a one-reload-per-build budget, namespaced by `scope`
 * (e.g. "chunk", "action") so unrelated recovery paths never share a slot.
 * Exported so the pre-hydration inline chunk-recovery script — which can't
 * import this module's runtime function, see `chunk-load-recovery.tsx` —
 * still derives the SAME key format instead of hand-duplicating it.
 */
export function reloadBudgetKey(scope: string, version: string): string {
	return `__ld_reload_${scope}_${version}`;
}

/**
 * Reload the page once per (scope, BUILD_VERSION) — sessionStorage is the
 * ledger. A new deploy ships a new BUILD_VERSION → a fresh budget; a
 * genuinely broken build reloads once per tab session, then the budget
 * sticks and the caller should surface the real error instead of looping.
 *
 * Returns true if a reload was triggered (caller should stop further
 * handling), false if the budget for this build was already spent.
 */
export function reloadOncePerBuild(scope: string): boolean {
	const key = reloadBudgetKey(scope, BUILD_VERSION);
	try {
		if (sessionStorage.getItem(key)) return false;
		sessionStorage.setItem(key, "1");
	} catch {
		// sessionStorage unavailable (private mode etc.) — reload once, no budget tracking.
	}
	window.location.reload();
	return true;
}
